"use client";

import {
  CREATOR_FUNNEL_EVENTS,
  LEGACY_WEB_APPLIED_EVENT,
  NFONE_SUPPORT_EMAIL,
} from "@newform/constants";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import {
  CreatorAuthFrame,
  CreatorAuthMessage,
} from "@/components/creatorPortal/CreatorAuthFrame";
import {
  DEPTH_ROOT_ID,
  QUIET_HOVER,
} from "@/components/creatorPortal/one/chrome";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Button } from "@/components/ui/design-system/button";
import { Progress } from "@/components/ui/design-system/progress";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { Body, Rail } from "@/components/ui/design-system/text";
import { useCrossfade } from "@/components/ui/design-system/use-crossfade";
import { readCreatorAcquisition } from "@/lib/analytics/creatorAcquisition";
import {
  canUseCreatorMetaPixel,
  trackMetaPixelEvent,
} from "@/lib/analytics/metaPixel";
import { trpc } from "@/lib/trpc-client";
import { cn } from "@/lib/utils";
import { AgreementSigningStep } from "./AgreementSigningStep";
import { ApplyAboutYouStep } from "./ApplyAboutYouStep";
import { ApplyInterestsStep } from "./ApplyInterestsStep";
import { ApplyOutcome } from "./ApplyOutcome";
import { ApplyStep, type StepDirection } from "./ApplyStep";
import { ApplyWorkStep } from "./ApplyWorkStep";
import {
  type ApplyFormErrorKey,
  type ApplyFormState,
  applicantFacingError,
  EMPTY_APPLY_FORM,
  type HandleProblem,
  MAX_INTEREST_AREAS,
  type SubmitOutcome,
  shouldTrackCreatorApplication,
  submitOutcomeOf,
  toggleInterestArea,
  validateApplyForm,
} from "./applyForm";
import {
  APPLY_STEPS,
  type ApplyStepId,
  firstStepWithErrors,
  stepHasErrors,
} from "./applySteps";
import { CreatorBonusCard } from "./CreatorBonusCard";

/**
 * The public creator application: intake, agreement, and click-to-sign, one
 * question at a time on the same page the creator will later sign in on.
 *
 * This component owns the form state, the step, and the submit; each step is
 * a presentational sibling, and `applySteps.ts` says which fields each one
 * answers for.
 */

export function ApplyFlow() {
  const t = useTranslations("apply");
  const agreementQuery = trpc.creatorApplication.getAgreement.useQuery(
    undefined,
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const [form, setForm] = useState<ApplyFormState>(EMPTY_APPLY_FORM);
  const [agreed, setAgreed] = useState(false);
  // Which agreement text was read to the end, not just whether one was: it
  // lives here with the other answers so Back and Forward keep it, and a
  // version republished while the tab is open arrives unread, and unagreed,
  // because it is a different string.
  const [readText, setReadText] = useState<string | null>(null);
  const agreementRead =
    readText !== null && readText === agreementQuery.data?.text;
  const agreedToThese = agreed && agreementRead;
  // A step's errors stay hidden until its Continue has been pressed once:
  // flagging an empty question the moment it appears reads as hostile.
  const [attempted, setAttempted] = useState<ReadonlySet<ApplyStepId>>(
    () => new Set(),
  );
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  // What to fix, when the server turned the application away by rule.
  const [handleProblems, setHandleProblems] = useState<HandleProblem[]>([]);
  // CROSSFADE, NOT A CUT, the same as the portal's submit screen: the form
  // leaving and the answer arriving are one event. The frame, wordmark and
  // footer hold still while only the middle trades places, so nothing that
  // stays the same appears to reload.
  const body = useCrossfade(outcome ? "done" : "form");

  const submit = trpc.creatorApplication.submit.useMutation();

  const errors = useMemo(
    () =>
      validateApplyForm(
        form,
        agreedToThese,
        agreementQuery.data
          ? {
              ageRanges: agreementQuery.data.ageRanges,
              interestAreas: agreementQuery.data.interestAreas,
            }
          : undefined,
      ),
    [form, agreedToThese, agreementQuery.data],
  );

  /*
    THE STEP LIVES IN THE URL, so the browser's own Back and Forward walk the
    questions, and a step is something you can return to rather than a state
    you lose. A search param rather than a route per step: a route change
    remounts the page and would throw away every answer, while this keeps one
    mounted form and only the question changes. The pixel's PageView follows
    the pathname, so steps don't inflate it either.

    The URL asks; the answers decide. You can be on a step only once every
    step before it passes, so a deep link, a reload (which starts the answers
    over) or a Forward past an answer you since emptied lands on the first
    step that still needs something, and the address is corrected to match.
  */
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const requestedIndex = Math.max(
    0,
    APPLY_STEPS.findIndex((candidate) => candidate.id === params.get("step")),
  );
  const firstBroken = firstStepWithErrors(errors);
  const reachableIndex =
    firstBroken === -1 ? APPLY_STEPS.length - 1 : firstBroken;
  const stepIndex = Math.min(requestedIndex, reachableIndex);

  // Which way the last move went, derived during render rather than in an
  // effect: the entering step reads its direction on the frame it mounts,
  // and an effect would hand it the old one. `depth` counts the steps this
  // tab pushed, so the in-page Back can be the browser's Back while there is
  // one of ours to return to, and never leaves the page when there isn't.
  const [motion, setMotion] = useState({
    index: stepIndex,
    direction: "forward" as StepDirection,
    // False until the first move, so the opening step doesn't steal focus
    // from wherever the browser put it on load.
    moved: false,
    depth: 0,
  });
  if (motion.index !== stepIndex) {
    const forward = stepIndex > motion.index;
    setMotion({
      index: stepIndex,
      direction: forward ? "forward" : "back",
      moved: true,
      depth: forward ? motion.depth + 1 : Math.max(0, motion.depth - 1),
    });
  }
  const { direction, moved } = motion;

  const hrefFor = (index: number) => {
    const next = new URLSearchParams(params.toString());
    if (index === 0) next.delete("step");
    else next.set("step", APPLY_STEPS[index].id);
    const query = next.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  const step = APPLY_STEPS[stepIndex];
  const lastStep = stepIndex === APPLY_STEPS.length - 1;
  // Read to the end, agreed, and signed with the matching name. Agreeing
  // already requires the scroll (the box stays locked until then), so the
  // agreement step's own checks are the whole condition.
  const readyToSign = !stepHasErrors(
    APPLY_STEPS[APPLY_STEPS.length - 1],
    errors,
  );
  // Chips have nothing to type into, so an error after the press would be
  // the first sign a choice was needed. Holding the button tells them sooner.
  const needsInterest =
    step.id === "interests" && form.interestAreas.length === 0;

  const onChange = <K extends keyof ApplyFormState>(
    key: K,
    value: ApplyFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const errorFor = (key: ApplyFormErrorKey) => {
    const message = attempted.has(step.id) ? errors[key] : undefined;
    return message ? t(message) : undefined;
  };

  const markAttempted = (id: ApplyStepId) =>
    setAttempted((prev) => new Set(prev).add(id));

  const goForward = () =>
    router.push(hrefFor(stepIndex + 1), { scroll: false });

  // Back to the handles with every other answer still filled in. The row they
  // just sent is REJECTED, which is the one state a new submission replaces,
  // so sending again from here is an ordinary re-application.
  const fixHandles = () => {
    setOutcome(null);
    router.replace(
      hrefFor(APPLY_STEPS.findIndex((candidate) => candidate.id === "work")),
      { scroll: false },
    );
  };

  const goBack = () => {
    if (motion.depth > 0) router.back();
    else router.replace(hrefFor(stepIndex - 1), { scroll: false });
  };

  // The address asked for further than the answers allow. Correct it rather
  // than leave a URL that names a step the page isn't showing, and if this
  // happened mid-session (a Forward past something since cleared), show why.
  useEffect(() => {
    if (requestedIndex <= stepIndex) return;
    router.replace(hrefFor(stepIndex), { scroll: false });
    if (motion.moved) markAttempted(APPLY_STEPS[stepIndex].id);
  }, [requestedIndex, stepIndex]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // The disabled button alone doesn't cover a fast double Enter-press in a
    // text field, which fires the handler again before React has reflected
    // the pending state in the DOM. The server absorbs the duplicate, but the
    // applicant would see an error rather than a submitting button.
    if (submit.isPending) return;
    if (stepHasErrors(step, errors)) {
      markAttempted(step.id);
      return;
    }
    if (!lastStep) {
      goForward();
      return;
    }
    if (firstBroken !== -1) {
      markAttempted(APPLY_STEPS[firstBroken].id);
      router.replace(hrefFor(firstBroken), { scroll: false });
      return;
    }
    const data = agreementQuery.data;
    if (!data) return;

    submit.mutate(
      {
        email: form.email.trim(),
        name: form.name.trim(),
        tiktokHandle: form.tiktokHandle.trim() || undefined,
        instagramHandle: form.instagramHandle.trim() || undefined,
        portfolioUrl: form.portfolioUrl.trim() || undefined,
        // validateApplyForm was handed these same lists above and rejected
        // anything outside them, so the narrowing is sound; the form holds them
        // as strings because the inputs do.
        ageRange: form.ageRange as (typeof data.ageRanges)[number],
        location: form.location.trim() || undefined,
        interestAreas:
          form.interestAreas as (typeof data.interestAreas)[number][],
        aboutYou: form.aboutYou.trim() || undefined,
        signatureName: form.signatureName.trim(),
        // Round-tripped rather than read from a local constant: this is what
        // makes a tab left open across a revision fail loudly instead of
        // recording acceptance of text the applicant never saw.
        agreementVersion: data.version,
        ...readCreatorAcquisition(),
        // Named on the server's copy of the application event, as the pixel does.
        pageUrl: `${window.location.origin}/join/apply`,
      },
      {
        onSuccess: (result) => {
          setOutcome(submitOutcomeOf(result));
          setHandleProblems(
            result.outcome === "received"
              ? (result.autoRejected?.problems ?? [])
              : [],
          );
          // Judged on what the server says it stored, not on this tab's
          // cached view of whether the funnel is open — those can disagree if
          // it is opened or closed while the form is up.
          if (
            result.outcome === "received" &&
            shouldTrackCreatorApplication(result) &&
            canUseCreatorMetaPixel()
          ) {
            trackMetaPixelEvent(CREATOR_FUNNEL_EVENTS.applied, {
              eventId: result.metaApplicationEventId,
            });
            trackMetaPixelEvent(LEGACY_WEB_APPLIED_EVENT);
          }
        },
      },
    );
  };

  if (agreementQuery.isPending) {
    return (
      <CreatorAuthFrame>
        <div className="flex flex-col items-center gap-nf-ds-6">
          <Skeleton className="h-nf-ds-1 w-full rounded-nf-ds-pill" />
          <Skeleton className="h-nf-ds-10 w-3/4" />
          <Skeleton className="h-nf-ds-5 w-full" />
          <Skeleton className="h-nf-ds-32 w-full" />
        </div>
      </CreatorAuthFrame>
    );
  }

  // Any failure to load is transient as far as the applicant is concerned —
  // reporting it as "applications are closed" is indistinguishable from the
  // real closed state and sends someone who just clicked an ad away for good.
  // Only a successful response saying `open: false` means closed.
  if (agreementQuery.isError) {
    const throttled = agreementQuery.error.data?.code === "TOO_MANY_REQUESTS";
    return (
      <CreatorAuthMessage
        wall={false}
        title={throttled ? t("load.busyTitle") : t("load.failedTitle")}
        body={throttled ? t("load.busyBody") : t("load.failedBody")}
      >
        <Button
          variant="ink"
          className="w-full"
          onClick={() => void agreementQuery.refetch()}
        >
          {t("load.retry")}
        </Button>
      </CreatorAuthMessage>
    );
  }

  if (!agreementQuery.data.open) {
    return (
      <CreatorAuthMessage
        wall={false}
        title={t("closed.title")}
        body={t("closed.body")}
      >
        <Button asChild variant="ink" className="w-full">
          <Link href="/join">{t("closed.back")}</Link>
        </Button>
      </CreatorAuthMessage>
    );
  }

  const { ageRanges, interestAreas, text: agreementText } = agreementQuery.data;
  const submitError = submit.error ? applicantFacingError(submit.error) : null;

  return (
    <CreatorAuthFrame id={DEPTH_ROOT_ID}>
      <div
        className={cn(
          "transition-opacity duration-nf-ds-3 ease-nf-ds-std",
          body.out && "opacity-0",
        )}
      >
        {body.shown === "done" && outcome ? (
          <ApplyOutcome
            outcome={outcome}
            email={form.email.trim()}
            supportEmail={NFONE_SUPPORT_EMAIL}
            handleProblems={handleProblems}
            onFixHandles={fixHandles}
          />
        ) : (
          <form
            onSubmit={onSubmit}
            className="flex flex-col gap-nf-ds-6"
            noValidate
          >
            {agreementQuery.data.staffPreview ? (
              // Staff walking the closed funnel. Says so unmistakably: the whole
              // point of the ships-dark gate is that nobody signs unreviewed terms
              // by accident, and that includes us.
              <Alert variant="warn">
                <AlertDescription>{t("staffPreview")}</AlertDescription>
              </Alert>
            ) : null}

            {/* Where am I and how do I get out, answered before the question.
            Back on the first step leaves for the program page rather than
            being disabled: a control that does nothing is a dead end. */}
            <div className="flex flex-col gap-nf-ds-3">
              <div className="flex items-center justify-between">
                {stepIndex === 0 ? (
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className={QUIET_HOVER}
                  >
                    <Link href="/join">
                      <ArrowLeft aria-hidden="true" className="size-4" />
                      {t("back")}
                    </Link>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={QUIET_HOVER}
                    disabled={submit.isPending}
                    onClick={goBack}
                  >
                    <ArrowLeft aria-hidden="true" className="size-4" />
                    {t("back")}
                  </Button>
                )}
                <Rail muted>
                  {t("stepCounter", {
                    current: stepIndex + 1,
                    total: APPLY_STEPS.length,
                  })}
                </Rail>
              </div>
              <Progress
                value={stepIndex + 1}
                max={APPLY_STEPS.length}
                aria-label={t("progressLabel")}
                className="bg-nf-ds-bg-muted"
              />
            </div>

            <ApplyStep
              key={step.id}
              title={t(step.title)}
              lede={t(step.lede, { max: MAX_INTEREST_AREAS })}
              direction={direction}
              takeFocus={moved}
            >
              {step.id === "about" ? (
                <>
                  <ApplyAboutYouStep
                    form={form}
                    ageRanges={ageRanges}
                    errorFor={errorFor}
                    onChange={onChange}
                  />
                  <CreatorBonusCard />
                </>
              ) : step.id === "work" ? (
                <ApplyWorkStep
                  form={form}
                  errorFor={errorFor}
                  onChange={onChange}
                />
              ) : step.id === "interests" ? (
                <ApplyInterestsStep
                  form={form}
                  interestAreas={interestAreas}
                  errorFor={errorFor}
                  onChange={onChange}
                  onToggleArea={(area) =>
                    setForm((prev) => ({
                      ...prev,
                      interestAreas: toggleInterestArea(
                        prev.interestAreas,
                        area,
                      ),
                    }))
                  }
                />
              ) : (
                <AgreementSigningStep
                  agreementText={agreementText}
                  agreed={agreedToThese}
                  agreedError={errorFor("agreed")}
                  onAgreedChange={setAgreed}
                  signerName={form.name}
                  signatureName={form.signatureName}
                  signatureError={errorFor("signatureName")}
                  onSignatureNameChange={(value) =>
                    onChange("signatureName", value)
                  }
                  read={agreementRead}
                  onRead={() => {
                    // A tick given to an earlier text doesn't carry over: the
                    // new one is agreed to by ticking it, not by reading it.
                    if (readText !== agreementText) setAgreed(false);
                    setReadText(agreementText);
                  }}
                />
              )}
            </ApplyStep>

            {submitError ? (
              <Alert variant="err" role="alert">
                <AlertDescription>
                  {"key" in submitError
                    ? t(submitError.key)
                    : submitError.message}
                </AlertDescription>
              </Alert>
            ) : null}

            {/* Outside the keyed step, so it holds still while the question
            changes: the one control that is always in the same place is the
            one a thumb can find without looking. */}
            <div className="flex flex-col items-center gap-nf-ds-3">
              <Button
                type="submit"
                variant="ink"
                disabled={
                  submit.isPending ||
                  needsInterest ||
                  (lastStep && !readyToSign)
                }
                className="w-full transition-[color,background-color,opacity,scale] duration-nf-ds-1 ease-nf-ds-out active:scale-97 motion-reduce:active:scale-100"
              >
                {lastStep
                  ? submit.isPending
                    ? t("submitting")
                    : t("signAndSubmit")
                  : t("continue")}
              </Button>
              {/* A disabled button owes an explanation, and this is where the eye
              lands when it will not press. */}
              {needsInterest ? (
                <Body muted className="text-nf-ds-sm">
                  {t("pickOneToContinue")}
                </Body>
              ) : null}
              {lastStep ? (
                <div aria-live="polite">
                  <Body muted className="text-nf-ds-sm">
                    {readyToSign
                      ? t("submitHint.ready")
                      : t("submitHint.notReady")}
                  </Body>
                </div>
              ) : null}
            </div>
          </form>
        )}
      </div>
    </CreatorAuthFrame>
  );
}
